import MoreVertIcon from '@mui/icons-material/MoreVert';
import { IconButton, Menu, MenuItem, TableCell, TableRow } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { TableTemplateCell } from '/@/components/bayer/table/template-cell';
import useUnitProvider from '/@/core/context/unit';
import { useAnchorEl } from '/@/hooks/anchor-el/useAnchorEl';
import { useGrowersDashboard } from '/@/hooks/dashboards';
import { convertAreaFromM2 } from '/@/hooks/geometry/area';
import { parseUserNode } from '/@/hooks/nodes';
import { _ } from '/@/locale';
import { getUser } from '/@/store/session/selectors';
import type { Grower } from '/@/types/growers';
import { getNodeScopeFilters } from '/@/utils/node-scope';
import { reverse } from '/@/utils/router';
import CropDistributionBar, {
    type CropDistributionItem,
} from '/@/views/dashboard/growers/components/growers-list/components/crop-distribution-bar';
import GrowerNameCell from '/@/views/dashboard/growers/components/growers-list/components/grower-name-cell';
import { getCrops } from '/@/views/dashboard/hooks/crops';

const CampaignRvRow = ({ rv, cropFilter }: { rv: Grower; cropFilter: string }) => {
    const navigate = useNavigate();
    const units = useUnitProvider();
    const user = useSelector(getUser);
    const growersDashboardQuery = useGrowersDashboard({
        node: rv.id,
        ...getNodeScopeFilters('rvs'),
    });

    const [{ open, anchorEl }, { handleClick, handleClose }] = useAnchorEl();

    // Transform API data to component format
    const transformGrowerData = useCallback(
        (grower: Grower) => {
            const parsedUserNode = parseUserNode(grower, user);
            const fields = rv.fields || {};
            const countByCrop = fields.countByCrop || {};
            const totalFields = fields.count || 0;

            // Calculate crop distribution
            const crops = getCrops();
            const cropDistribution: CropDistributionItem[] = [];
            if (totalFields > 0) {
                Object.entries(countByCrop).forEach(([crop, fields]) => {
                    const cropData = crops[crop as keyof typeof crops];
                    if (cropData && Number(fields) > 0) {
                        cropDistribution.push({
                            crop,
                            getChip: cropData?.getChip,
                            percentage: Math.round((Number(fields) / totalFields) * 100),
                            fields: Number(fields),
                            icon: cropData?.icon,
                            color: cropData?.color,
                            label: cropData?.name,
                        });
                    }
                });

                // Sort by order from getCrops
                cropDistribution.sort((a, b) => {
                    const cropA = crops[a.crop as keyof typeof crops];
                    const cropB = crops[b.crop as keyof typeof crops];
                    return (cropA?.order || 999) - (cropB?.order || 999);
                });
            }

            // Generate suggestions

            const fieldviewConnected = rv.user?.authProviders?.includes('fieldview');

            return {
                id: rv.id,
                name: parsedUserNode.isSelf ? _('Mi cuenta') : parsedUserNode.name,
                location: parsedUserNode.location,
                totalFields,
                totalArea: convertAreaFromM2(grower?.fields.totalArea || 0, units).toString(),
                fieldviewConnected: fieldviewConnected,
                hasAssignedUser: rv.user !== null,
                cropDistribution,
                phone: parsedUserNode.phone,
                email: parsedUserNode.email,
                isSelf: parsedUserNode.isSelf,
                totalGrowersCount: growersDashboardQuery.data?.count || 0,
                totalGrowersCountWithFieldview: growersDashboardQuery.data?.withFieldview || 0,
            };
        },
        [units, user, growersDashboardQuery.data, rv]
    );

    const transformedGrower = useMemo(() => transformGrowerData(rv), [rv, transformGrowerData]);

    return (
        <TableRow
            key={transformedGrower.id}
            sx={{
                '&:last-child td, &:last-child th': { border: 0 },
                cursor: 'pointer',
                '&:hover': { backgroundColor: 'action.hover' },
            }}
            onClick={() => {
                navigate(
                    reverse('RVs.detail', {
                        params: {
                            rvId: transformedGrower.id,
                        },
                        qs: {
                            tab: 'current-campaign',
                        },
                    })
                );
            }}
        >
            <TableCell width='20%'>
                <GrowerNameCell
                    name={transformedGrower.name}
                    location={transformedGrower.location}
                    hasAssignedUser={transformedGrower.hasAssignedUser}
                />
            </TableCell>
            <TableCell width='20%'>
                <TableTemplateCell
                    title={_('{{count}} Productores', { count: transformedGrower.totalGrowersCount })}
                    subtitle={_('{{value}} con Fieldview', { value: transformedGrower.totalGrowersCountWithFieldview })}
                />
            </TableCell>
            <TableCell width='20%'>
                <TableTemplateCell
                    title={_('{{count}} Lote', { count: transformedGrower.totalFields })}
                    subtitle={_('Superficie total: {{value}}', { value: transformedGrower.totalArea })}
                />
            </TableCell>
            <TableCell width='30%'>
                {transformedGrower.cropDistribution.length > 0 ? (
                    <CropDistributionBar distribution={transformedGrower.cropDistribution} cropFilter={cropFilter} />
                ) : null}
            </TableCell>
            <TableCell align='right' onClick={(e) => e.stopPropagation()}>
                <IconButton
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation();
                        handleClick(e);
                    }}
                >
                    <MoreVertIcon />
                </IconButton>
                <Menu
                    open={!!anchorEl}
                    onClose={(e) => {
                        e.stopPropagation();
                        handleClose();
                    }}
                    anchorEl={anchorEl}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                    }}
                >
                    {[
                        {
                            label: _('Ir al detalle del RV'),
                            onClick: () => {
                                navigate(
                                    reverse('RVs.detail', {
                                        params: {
                                            rvId: transformedGrower.id,
                                        },
                                    })
                                );
                            },
                            disabled: false,
                        },
                    ].map((item, index) => (
                        <MenuItem
                            key={index}
                            onClick={(e) => {
                                e.stopPropagation();
                                item.onClick(e);
                                handleClose();
                            }}
                            disabled={item.disabled}
                        >
                            {item.label}
                        </MenuItem>
                    ))}
                </Menu>
            </TableCell>
        </TableRow>
    );
};

export default CampaignRvRow;
